import React, { useEffect, useState, useCallback, useContext } from 'react'
import { CHART_TYPES, CHART_MODES, chartMock, TYPE_VALUES, CHART_COLOR_PALETTE } from './mock'
import ChartView from './components/ChartView'
import ChartHeader from './components/ChartHeader'
import ChartTypes from './components/ChartTypes'
import StackSizes from './components/StackSizes'
import Positions from './components/Positions'
import Actions from './components/Actions'
import Sequence from './components/Sequence'
import OppPositions from './components/OppPositions'
import './PreflopCharts.scss'
import { getChart } from './api'
import { usePrevious } from '../../hooks/usePrevious'
import { isEqual } from 'lodash'
import { getPreflopData } from '../ICMCharts/api'
import { useLocation } from 'react-router-dom'
import FilterToggle from './components/FilterToggle'
import { AppContext } from '../../AppContext'
import SequenceSidebar from './components/SequenceSidebar'
import Settings from "./components/Settings";
import { ReactComponent as ChooseArrowsImg } from './images/choose_arrows.svg';

const convertOptionText = (str) => {
  let formattedStr = str.replace(/([A-Z])/g, ' $1').trim();
  return formattedStr.charAt(0).toUpperCase() + formattedStr.slice(1).toLowerCase();
};

const PageTitle = ({ toggleFilters, isFiltersExpanded, colorPaletteValue, setColorPaletteValue, showColorPalette, setShowColorPalette }) => {
  return (
    <div className="PreflopCharts__title-container">
      <h2 className="PreflopCharts__title">GTO Preflop Chart</h2>
      <div className="PreflopCharts__settings">
        <div className="PreflopCharts__settings-row">
          <FilterToggle
            isFiltersExpanded={isFiltersExpanded}
            toggleFilters={toggleFilters}
          />
        </div>
        <div className="PreflopCharts__settings-row">
          <div className={"FilterToggle__label"}>Colors{" "}
            <span className={`settings${showColorPalette ? ' active' : ''}`}
                  onClick={() => setShowColorPalette(!showColorPalette)}>
          <i className="fa fa-question-circle" aria-hidden="true"></i>
        </span>
          </div>
          <div className={"PreflopCharts__color"}>
            <select onChange={(e) => setColorPaletteValue(e.target.value)} value={colorPaletteValue}>
              {Object.keys(CHART_COLOR_PALETTE).map((color) => (
                <option value={color} key={color}>{convertOptionText(color)}</option>
              ))}
            </select>
            <ChooseArrowsImg/>
          </div>
        </div>
      </div>
    </div>
  )
}

export const PreflopCharts = () => {
  const [loading, setLoading] = useState(false)
  const [seqData, setSeqData] = useState({
    chartType: 'preflop_6max_cash',
    stackSizes: 100,
    position_id: 0
  })
  const [selectedFilters, setSelectedFilters] = useState({
    chartType: 'MTTs Full Ring',
    stack: null,
    position: null,
    action: null,
    oppos: null,
  })
  const [chartData, setChartData] = useState({
    chartHTML: null,
    chartName: '',
    chartActions: [],
  })
  const [filters, setFilters] = useState({
    actions: [],
    opponents: [],
    positions: [],
    stackSizes: [],
  })
  const [config, setConfig] = useState({
    colors: null,
    blindRestrictions: null,
    huRestrictions: null,
    icmRestrictions: null,
  })
  const { colors } = config
  const [filterMocks, setFilterMocks] = useState({
    oppPositions: [],
    vsActions: [],
  })
  const { stackSizes, positions, opponents, actions } = filters
  const [isSeqModeExpanded, setIsSeqModeExpanded] = useState(false)

  const showAccessPopup = (requiredAccess) => {
    window.showPopupPlan({
      title: "Start your PokerCoaching membership today to unlock this content!",
      planSet:
        requiredAccess === "pc"
          ? "standard-premium"
          : requiredAccess === "pcp"
          ? "premium"
          : "free",
      userAccess: user.type,
    });
  }

  const { user } = useContext(AppContext)

  const { type: userAccess } = user

  const [isFiltersExpanded, setIsFiltersExpanded] = useState(true)
  const { search } = useLocation()

  const [colorPaletteValue, setColorPaletteValue] = useState(localStorage.getItem('colorConfig') || 'default');

  const savedColorConfig = () => {
    const savedColorConfig = localStorage.getItem('colorConfig');
    if (savedColorConfig) {
      return (savedColorConfig === 'custom') ? JSON.parse(localStorage.getItem('customColorConfig')) : CHART_COLOR_PALETTE[savedColorConfig];
    }
    return CHART_COLOR_PALETTE['default'];
  }

  const [colorsConfig, setColorsConfig] = useState(savedColorConfig());
  const [showColorPalette, setShowColorPalette] = useState(false)

  useEffect(() => {
    if(colorPaletteValue !== 'custom'){
      setColorsConfig(CHART_COLOR_PALETTE[colorPaletteValue]);
    }
    setShowColorPalette(colorPaletteValue === 'custom');
    localStorage.setItem('colorConfig', colorPaletteValue);
  }, [colorPaletteValue]);

  const handleColorChange = (colorName, newColor) => {
    setColorsConfig(prevColorsConfig => ({
      ...prevColorsConfig,
      [colorName]: newColor,
    }));
  };

  useEffect(() => {
    localStorage.setItem('customColorConfig', JSON.stringify(colorsConfig));
  }, [colorsConfig]);

  const getChartColors = useCallback(async () => {
    const data = await getPreflopData()
    const {
      color_pallet: colors,
      access_blind_restrict: blindRestrictions,
      access_hu_restrict: huRestrictions,
      access_icm_restrict: icmRestrictions,
    } = data.settings

    setConfig({
      colors,
      blindRestrictions,
      huRestrictions,
      icmRestrictions,
    })
  }, [setConfig])

  useEffect(() => {
    getChartColors()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getInitialData = async (type) => {
    if (loading) return
    setLoading(true)
    const data = await getChart({
      chartMode: CHART_MODES.GTO,
      type: CHART_TYPES[type],
    })
    const {
      init_filter: { stacks, positions, oppPositions, vsActions },
    } = data
    setFilterMocks({
      oppPositions,
      vsActions,
    })
    const stackSizes = Object.values(stacks).sort((a, b) => a - b)

    setFilters({ ...filters, stackSizes, positions })
    setChartData({ chartHTML: chartMock })

    setSelectedFilters({
      ...selectedFilters,
      chartType: type,
      stack: null,
      position: null,
      action: null,
      oppos: null,
    })
    setLoading(false)
  }

  const prevSelectedFilters = usePrevious(selectedFilters)
  const prevChartType = usePrevious(selectedFilters.chartType)

  const getData = useCallback(async () => {
    if (selectedFilters.chartType !== prevChartType) return

    if (!isEqual(prevSelectedFilters, selectedFilters)) {
      setLoading(true)

      const data = await getChart({
        chartMode: CHART_MODES.GTO,
        type: CHART_TYPES[selectedFilters.chartType],
        filter: selectedFilters,
        isJson: isSeqModeExpanded,
      })
      if (!data.success) {
        alert('No data found')
        setLoading(false)
        return
      }
      const {
        chart_html: chartHTML,
        chart_title: chartName,
        filter_curr: filterCurr,
        filter: { actions_json: actions, oppos_json: opponents },
      } = data

      let filterChartType = filterCurr.chartType
      if (filterChartType === 'Cash Game 8max') filterChartType = 'CASH GAME'
      setSelectedFilters({ ...filterCurr, chartType: filterChartType })
      setChartData({ chartHTML, chartName })
      setFilters({ ...filters, actions, opponents })
      setLoading(false)

      if (actions.includes('RFI') && !selectedFilters.action)
        setSelectedFilters({
          ...filterCurr,
          action: 'RFI',
          chartType: filterChartType,
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters, prevSelectedFilters, isSeqModeExpanded])

  useEffect(() => {
    const typeParam = search.split('?type=')[1]
    let type = null
    if (typeParam) {
      type = typeParam.split('&')[0]
    }
    if (!type) {
      return
    }
    const typeValue = TYPE_VALUES[type]
    setSelectedFilters({ ...selectedFilters, chartType: typeValue })
    getInitialData(typeValue)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  useEffect(() => {
    if (!search) getInitialData('MTTs Full Ring')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (selectedFilters.stack && !loading) getData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters])

  const sendChartOptionSelectToAnalytics = (value, type) => {
    // actid=32
    window.analytics?.track('chart_option:click', {
      type: type,
      value: value,
    })
  }

  useEffect(() => {
    getInitialData('MTTs Full Ring')
  },[isSeqModeExpanded])

  return (
    <div className="PreflopCharts">
      <PageTitle
        isFiltersExpanded={isFiltersExpanded}
        toggleFilters={() => {
          setIsFiltersExpanded(!isFiltersExpanded)
          if(!isFiltersExpanded){
            setIsSeqModeExpanded(false)
          }
        }}
        isSeqModeExpanded={isSeqModeExpanded}
        colorPaletteValue={colorPaletteValue}
        setColorPaletteValue={setColorPaletteValue}
        showColorPalette={showColorPalette}
        setShowColorPalette={setShowColorPalette}
      />

      {showColorPalette && <Settings
        onColorChange={handleColorChange}
        colorsConfig={colorsConfig}
        setColorsConfig={setColorsConfig}
        readOnly={(colorPaletteValue !== 'custom')}
      />}

      {isSeqModeExpanded && <Sequence
        setChartData={setChartData}
        chartData={chartData}
        selectedFilters={selectedFilters}
        showAccessPopup={showAccessPopup}
        userAccess={userAccess}
        seqData={seqData}
        setSeqData={setSeqData}
      />}

      <div
        className={`PreflopCharts__container ${
          isFiltersExpanded ? 'expanded' : ''
        }`}
        style={{ display: 'flex', flexDirection: 'row' }}
      >
        <div
          className={`PreflopCharts__chart-filters ${
            isFiltersExpanded ? 'expanded' : ''
          }`}
          style={{
            display: 'flex',
            flexDirection: 'column',
            marginRight: (!isFiltersExpanded) ? '64px' : '40px',
            flexGrow: 1,
            rowGap: '15px',
          }}
        >
          <ChartTypes
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            setIsSeqModeExpanded={setIsSeqModeExpanded}
            setIsFiltersExpanded={setIsFiltersExpanded}
          />
          <StackSizes
            stackSizes={stackSizes}
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            config={config}
            userAccess={userAccess}
            showAccessPopup={showAccessPopup}
            sendChartOptionSelectToAnalytics={(value) =>
              sendChartOptionSelectToAnalytics(value, 'Stack Size, bbs')
            }
          />
          <Positions
            positions={positions}
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            sendChartOptionSelectToAnalytics={(value) =>
              sendChartOptionSelectToAnalytics(value, 'Hero position')
            }
          />
          <Actions
            actionsMock={filterMocks.vsActions}
            actions={actions}
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            sendChartOptionSelectToAnalytics={(value) =>
              sendChartOptionSelectToAnalytics(value, 'Action')
            }
          />
          <OppPositions
            oppPositionsMock={filterMocks.oppPositions}
            opponents={opponents}
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            sendChartOptionSelectToAnalytics={(value) =>
              sendChartOptionSelectToAnalytics(value, 'Opponent Position')
            }
          />
        </div>
        <div className={isSeqModeExpanded && 'sequence-mode'} style={colorsConfig}>
          {!isSeqModeExpanded && <ChartHeader selectedFilters={selectedFilters}/>}
          <ChartView
            isFiltersExpanded={isFiltersExpanded}
            colors={colors}
            chartHTML={chartData.chartHTML}
          />

          {isSeqModeExpanded && (
            <div className={"sequence-sidebar"}>
              <SequenceSidebar seqData={seqData} chartData={chartData}/>
            </div>
          )}

        </div>
      </div>
    </div>
  )
}
